import React, { useState, useEffect } from 'react';
import { Users, ChevronRight, Mail, Phone, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

const CandidatePipeline = ({ onUpdate }) => {
  const [candidates, setCandidates] = useState([]);
  const [stages] = useState(['Applied', 'Screening', 'Interview', 'Offer', 'Hired', 'Rejected']);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/candidates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const updateStage = async (candidateId, newStage) => {
    try {
      const token = localStorage.getItem('shansoats_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/candidates/${candidateId}/stage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage })
      });

      fetchCandidates();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const getCandidatesByStage = (stage) => {
    return candidates.filter(c => c.stage === stage);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Applied': 'blue',
      'Screening': 'yellow',
      'Interview': 'purple',
      'Offer': 'green',
      'Hired': 'emerald',
      'Rejected': 'red'
    };
    return colors[stage] || 'gray';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-8 h-8 text-purple-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">Candidate Pipeline</h2>
          <p className="text-gray-400">Track candidates through recruitment stages</p>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageCandidates = getCandidatesByStage(stage);
          const color = getStageColor(stage);
          
          return (
            <div key={stage} className="flex-shrink-0 w-80">
              <div className={`bg-${color}-500/10 border border-${color}-500/30 rounded-lg p-3 mb-3`}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-bold text-${color}-400`}>{stage}</h3>
                  <span className={`text-sm font-semibold text-${color}-400`}>{stageCandidates.length}</span>
                </div>
              </div>

              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {stageCandidates.length === 0 ? (
                  <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">No candidates</p>
                  </div>
                ) : (
                  stageCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-cyan-500/50 transition-all cursor-pointer"
                      onClick={() => setSelectedCandidate(candidate)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-white font-semibold">{candidate.name}</h4>
                          <p className="text-sm text-gray-400">{candidate.email}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-400 text-lg">★</span>
                          <span className="text-sm text-white font-semibold">{candidate.match_score || 0}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{candidate.job_title}</p>
                      <div className="flex gap-2">
                        {stage !== 'Hired' && stage !== 'Rejected' && (
                          <select
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateStage(candidate.id, e.target.value);
                            }}
                            value={stage}
                            className="flex-1 px-2 py-1 text-xs bg-gray-900 border border-gray-700 rounded text-white focus:outline-none"
                          >
                            {stages.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedCandidate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4" onClick={() => setSelectedCandidate(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold text-white mb-4">{selectedCandidate.name}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-300">
                <Mail className="w-4 h-4 text-cyan-400" />
                <span>{selectedCandidate.email}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span>Applied: {new Date(selectedCandidate.created_at).toLocaleDateString()}</span>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-300">{selectedCandidate.summary || 'No summary available'}</p>
              </div>
            </div>
            <Button
              onClick={() => setSelectedCandidate(null)}
              className="w-full mt-6 bg-gray-700 hover:bg-gray-600"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatePipeline;
